import "../../styles/blocks/tabs/tabs.scss";

import React, {useCallback, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import "../../styles/ui/native-animations.scss";
import Fade from "../Animations/Fade";
import {Tab} from './Tab';
import KeyEventHandler from "../utils/KeyEventHandler/KeyEventHandler";
import Key from '../../helpers/KeyCodes'

/**
 * @param tabsItems - The items that map to each Tab.
 * @param {number=} initSelected - The index of the currently selected Tab.
 * @param {function} onSelect - Optional callback invoked when a Tab becomes selected.
 * @param {string} tabsClass - Class for tabs container element
 * @param {string} bodyClass - Class for body container element
 * @param {boolean=} isAutomatic - Automatic tab opening when focus is set
 * @returns {JSX.Element}
 * @constructor
 */
const Tabs = ({
  tabs: tabsItems,
  selected: initSelected = 0,
  onSelect,
  tabsClass,
  bodyClass,
  isAutomatic = true,
}) => {
  const [selected, setSelected] = useState(initSelected);
  const [hasSelected, setHasSelected] = useState(false);
  const [focused, setFocused] = useState(initSelected);
  const tabsRef = useRef(null);

  const selectTab = useCallback(index => {
    setHasSelected(true);
    setSelected(index);
  }, []);

  const handleTabClick = useCallback(
    (id) => {
      const tab = tabsItems.find((aTab) => aTab.id === id);

      if (tab == null) {
        return null;
      }

      const selectedIndex = tabsItems.indexOf(tab);
      selectTab(selectedIndex);
      setFocused(selectedIndex);
      onSelect?.(selectedIndex);
      tab.onAction?.(id);
    },
    [tabsItems, onSelect, selectTab],
  );

  const setActivePrevTab = useCallback(() => {
    const currentFocusIndex = isAutomatic ? selected : focused;

    const isFirstTab = currentFocusIndex === 0;
    let targetTabIndex = currentFocusIndex;

    if (!isFirstTab) {
      for (let i = currentFocusIndex - 1; i >= 0; i--) {
        if (!tabsItems[i].disabled) {
          targetTabIndex = i;
          break;
        }
      }
    }

    if (!isFirstTab && targetTabIndex >= 0) {
      if (isAutomatic) {
        selectTab(targetTabIndex);
      }
      setFocused(targetTabIndex);
    }
  }, [selected, focused, isAutomatic, tabsItems, selectTab]);

  const setActiveNextTab = useCallback(() => {
    const currentFocusIndex = isAutomatic ? selected : focused;

    const isLastTab = currentFocusIndex === tabsItems.length - 1;
    let targetTabIndex = currentFocusIndex;

    if (!isLastTab) {
      for (let i = currentFocusIndex + 1; i < tabsItems.length; i++) {
        if (!tabsItems[i].disabled) {
          targetTabIndex = i;
          break;
        }
      }
    }

    if (!isLastTab && targetTabIndex >= 0) {
      if (isAutomatic) {
        selectTab(targetTabIndex);
      }
      setFocused(targetTabIndex);
    }
  }, [selected, focused, isAutomatic, tabsItems, selectTab]);

  const selectFocusedTab = useCallback(() => {
    selectTab(focused);
    focusTabID(tabsItems[focused]?.id);
  }, [focused, tabsItems, selectTab]);

  useEffect(() => {
    focusTabID(tabsItems[focused]?.id);
  }, [focused, tabsItems]);

  return (
    <div className='tabs'>
      <KeyEventHandler
        keyCode={[Key.Enter, Key.Space]}
        event="keydown"
        handler={selectFocusedTab}
        targetRef={tabsRef}
      >
        <KeyEventHandler
          keyCode={[Key.LeftArrow, Key.UpArrow]}
          event="keydown"
          handler={setActivePrevTab}
          targetRef={tabsRef}
        >
          <KeyEventHandler
            keyCode={[Key.RightArrow, Key.DownArrow]}
            event="keydown"
            handler={setActiveNextTab}
            targetRef={tabsRef}
          >
            <ul
                className={cn(
                    'tabs__header',
                    tabsClass
                )}
                role="tablist"
                ref={tabsRef}
            >
              {tabsItems.map((_tab, index) => (
                <Tab
                  {..._tab}
                  key={`${index}-${_tab.id}`}
                  selected={index === selected}
                  onAction={() => handleTabClick(_tab.id)}
                />
              ))}
            </ul>
          </KeyEventHandler>
        </KeyEventHandler>
      </KeyEventHandler>

      <div>
        <div className={cn(
            bodyClass,
            'tabs__content-container'
        )}>
          {tabsItems.map((_tab, index) => (
            <div
              className={cn(
                  'tabs__content',
                selected === index && 'tabs__content--selected',
              )}
              key={`${_tab.id}-panel`}
            >
              <Fade
                visible={selected === index}
                animate={hasSelected}
                className={_tab.bodyClass}
                id={`panel-${_tab.id}`}
                role="tabpanel"
                tabIndex={0}
              >
                {_tab.body}
              </Fade>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const focusTabID = (tabID) => {
  const tab = document.getElementById(tabID);

  if (tab) {
    if (tab.tabIndex === -1) {
      tab.tabIndex = 0;
    }

    tab.focus({preventScroll: true});
  }
};
export default Tabs;
